import { liveMeetings } from '~/data/meetings'
import type { Meeting, Person, WorldSnapshot } from '~/data/types'
import { getRoomOffsets } from '~/game/arrange'
// office floor-plan layout

export interface RoomLayout {
  id: string
  name: string
  category: string
  x: number
  y: number
  w: number
  h: number
  boardH: number
  /** The live meeting gathered in this room (MEETING rooms only; null = idle). */
  meeting?: Meeting | null
}

export interface LoungeLayout {
  /** Centre of the social rug (world space). */
  cx: number
  cy: number
  w: number
  h: number
  /** Where the cooler sprite stands (top-right edge of the rug). */
  coolerX: number
  coolerY: number
  /** A few loose standing spots on the rug for people to gather around. */
  spots: ReadonlyArray<{ x: number; y: number }>
}

export interface Placement {
  userId: string
  roomId: string
  homeX: number
  homeY: number
  variant: number
  /** Seated at a shared conference table — no personal desk drawn. */
  atMeetingTable?: boolean
  /** Meeting at their own desk because the shared rooms were full. */
  meetingAtDesk?: boolean
  meetingName?: string
}

export interface VacantSeat {
  userId: string
  name: string
  roomId: string
  homeX: number
  homeY: number
  variant: number
  /** The owner's home desk while they're away in the meeting room. */
  inMeetingRoom?: boolean
  meetingName?: string
}

export interface WorldLayout {
  rooms: RoomLayout[]
  placements: Placement[]
  vacant: VacantSeat[]
  byUser: Record<string, Placement>
  roomById: Record<string, RoomLayout>
  lounge: LoungeLayout
  /** One reserved "+ hire an agent" desk slot per team room. */
  hireSpots: Array<{ roomId: string; x: number; y: number }>
  /** Seats hidden past a hall's cap (Commons/Agent Bay in a whale org):
   *  roomId → how many more people belong here than are drawn. */
  overflowByRoom: Record<string, number>
  worldW: number
  worldH: number
}

/** Which desk setup a person uses in a room — deterministic so the layout
 *  rebuilds identically. Keep DESK_VARIANTS in sync with furniture.ts. */
const DESK_VARIANTS = 5
function seatVariant(userId: string, roomId: string): number {
  const s = userId + roomId
  let sum = 0
  for (let i = 0; i < s.length; i += 1) sum += s.charCodeAt(i)
  return sum % DESK_VARIANTS
}

export const LOBBY_ID = '__lobby__'
export const AGENTS_ID = '__agents__'
export const MEETING_PREFIX = '__meeting__'
/** Most seats a catch-all hall (Commons / Agent Bay) will draw — a whale org
 *  would otherwise spawn hundreds of characters; the rest become a "+N more"
 *  sign and stay findable through the People directory. */
const HALL_CAP = 48
// A small bank of shared meeting rooms; live meetings beyond this fall back to
// meeting at the attendees' own desks.
const MEETING_TABLES = 3
// Texture-space seat centres of the 8 baked chairs on the conference-table
// sprite (132x64) — so attendees land exactly on the chairs.
const MEETING_SEATS: ReadonlyArray<readonly [number, number]> = [
  [34, 22],
  [64, 22],
  [94, 22],
  [10, 33],
  [122, 33],
  [34, 48],
  [64, 48],
  [94, 48],
]

const DESK = 84
const ROOM_PAD = 30
export const HEADER = 62
export const BOARD_H = 78
/** A tidy two-line wall board for MEETING rooms (current + next). Smaller than
 *  the team BOARD_H feed — it's a status strip, not a scrolling feed. */
export const MEETING_BOARD_H = 34
const GAP = 46
const MARGIN = 60
// 1:1 room footprint (must match the DM branch in the sizing pass below).
// Compact on purpose — the wing holds EVERY 1:1, so each room is a real but
// small two-person room, not a shrunken team room.
const DM_W = 120
const DM_H = 112

/**
 * The floor should fill the screen it's viewed on, not scroll forever: the
 * team grid's row width is chosen so the WHOLE floor (team grid + meeting
 * band + DM band) lands near the viewport's aspect ratio. Detected ONCE at
 * load — computeLayout stays deterministic across scene rebuilds within a
 * session (GameView and OfficeScene each compute the layout and must agree).
 * Portrait phones (~0.55) naturally get the old tall stack; desktop gets
 * ~16:9-ish wide floors; clamped so ultrawide doesn't become one endless row.
 */
const TARGET_ASPECT = ((): number => {
  try {
    const w = window.innerWidth
    const h = window.innerHeight
    if (!w || !h) return 16 / 9
    return Math.min(2.4, Math.max(0.55, w / h))
  } catch {
    return 16 / 9
  }
})()

/**
 * Turn a snapshot into a floor plan: team pulses become rooms, each person gets
 * a home desk in the team room they most recently visited (real membership +
 * real last_visited), and anyone unplaced lands in a shared Lobby. Pure +
 * deterministic so the scene can rebuild identically.
 */
export function computeLayout(snapshot: WorldSnapshot): WorldLayout {
  // Only TEAM pulses are rooms (DMs and personal pulses aren't collaborative
  // spaces). A room must have members to be drawn.
  const teamRooms = snapshot.pulses.filter(
    (p) => p.category === 'TEAM' && p.memberUserIds.length > 0,
  )

  const personById = new Map(snapshot.people.map((p) => [p.userId, p]))
  const visitOf = (
    room: { memberVisits?: Record<string, number | null> },
    userId: string,
  ) => room.memberVisits?.[userId] ?? null

  // Each person's HOME room = the team pulse they most recently visited (real
  // last_visited); fall back to the emptiest room when there's no visit data.
  // INVITED (not-yet-joined) accounts are skipped. Everyone else with no
  // visible team room stays on the floor anyway — they're still colleagues,
  // just ones you share nothing active with: humans lounge in the Commons,
  // agents idle in the Agent Bay, whatever their presence (asleep is fine).
  const activeRoom = new Map<string, string>()
  const activeCount = new Map<string, number>()
  const lobby: Person[] = []
  const agentBay: Person[] = []
  for (const person of snapshot.people) {
    if (person.status === 'INVITED') continue
    const mine = teamRooms.filter((r) => r.memberUserIds.includes(person.userId))
    if (mine.length === 0) {
      ;(person.isAgent ? agentBay : lobby).push(person)
      continue
    }
    let best = mine[0]
    let bestVisit = visitOf(best, person.userId)
    for (const room of mine) {
      const v = visitOf(room, person.userId)
      if (v != null && (bestVisit == null || v > bestVisit)) {
        best = room
        bestVisit = v
      }
    }
    if (bestVisit == null) {
      best = mine[0]
      for (const room of mine) {
        if ((activeCount.get(room.id) ?? 0) < (activeCount.get(best.id) ?? 0)) {
          best = room
        }
      }
    }
    activeRoom.set(person.userId, best.id)
    activeCount.set(best.id, (activeCount.get(best.id) ?? 0) + 1)
  }

  // 1:1 (DM) pulses are real rooms too — ALL of them, uncapped (they used to
  // silently stop at 12), living in the 1:1 wing at the bottom of the
  // building. Recently active first, so live conversations sit at the wing's
  // entrance and stale ones drift toward the back of the corridor.
  const dmRooms = snapshot.pulses
    .filter((p) => p.category === 'ONETOONE' && p.teamThreadId)
    .sort((a, b) => {
      const ta = a.latestUpdate ? Date.parse(a.latestUpdate) || 0 : 0
      const tb = b.latestUpdate ? Date.parse(b.latestUpdate) || 0 : 0
      if (tb !== ta) return tb - ta
      return (b.unreadCount ?? 0) - (a.unreadCount ?? 0)
    })

  // --- Meetings: a live session claims a conference table if one's free, else
  // its attendees meet at their own desks. A person is in at most one meeting.
  const live = liveMeetings(snapshot.meetings, Date.now())
  const tableMeetings: Array<{ meeting: Meeting; userIds: string[] }> = []
  const meetingAtDeskUsers = new Map<string, string>()
  const inMeetingRoomUsers = new Map<string, string>()
  const claimed = new Set<string>()
  for (const m of live) {
    // Seat the INVITED set (event invite list ∪ joined) — session attendee
    // rows alone left the table empty until people connected their bots.
    const attendees = m.invitedUserIds.filter(
      (u) => activeRoom.has(u) && !claimed.has(u),
    )
    // Pulse-calendar sessions often report NO attendee rows (the API only
    // tracks them once the companion joins) — the meeting is still real, so
    // it still claims a room: name + live board, chairs empty and honest.
    if (attendees.length === 0 && tableMeetings.length >= MEETING_TABLES) {
      continue
    }
    if (tableMeetings.length < MEETING_TABLES) {
      // A conference table seats at most the 8 baked chairs; any extra attendees
      // overflow to their own desks (meetingAtDesk) so no one stacks on a chair.
      const toTable = attendees.slice(0, MEETING_SEATS.length)
      const overflow = attendees.slice(MEETING_SEATS.length)
      tableMeetings.push({ meeting: m, userIds: toTable })
      toTable.forEach((u) => {
        claimed.add(u)
        inMeetingRoomUsers.set(u, m.name)
      })
      overflow.forEach((u) => {
        claimed.add(u)
        meetingAtDeskUsers.set(u, m.name)
      })
    } else {
      attendees.forEach((u) => {
        claimed.add(u)
        meetingAtDeskUsers.set(u, m.name)
      })
    }
  }

  // Every team room shows ALL its members: those whose home room this is sit
  // here (occupied), the rest get an empty named chair waiting for them.
  const presenceRank: Record<string, number> = {
    online: 0,
    away: 1,
    unknown: 2,
    offline: 3,
  }
  interface Seat {
    person: Person
    occupied: boolean
    inMeetingRoom?: boolean
    meetingAtDesk?: boolean
    atMeetingTable?: boolean
    meetingName?: string
  }
  const orderSeats = (seats: Seat[]): Seat[] =>
    seats.sort((a, b) => {
      if (a.occupied !== b.occupied) return a.occupied ? -1 : 1
      if (a.person.isAgent !== b.person.isAgent) return a.person.isAgent ? -1 : 1
      const pa = presenceRank[a.person.presence] ?? 2
      const pb = presenceRank[b.person.presence] ?? 2
      if (pa !== pb) return pa - pb
      return a.person.name.localeCompare(b.person.name)
    })
  const seatsByRoom = new Map<string, Seat[]>()
  for (const room of teamRooms) {
    const seats: Seat[] = []
    for (const uid of room.memberUserIds) {
      const person = personById.get(uid)
      if (!person || person.status === 'INVITED') continue
      const isHome = activeRoom.get(uid) === room.id
      if (isHome && inMeetingRoomUsers.has(uid)) {
        // Away at a conference table — leave a signed, empty desk behind.
        seats.push({
          person,
          occupied: false,
          inMeetingRoom: true,
          meetingName: inMeetingRoomUsers.get(uid),
        })
      } else if (isHome && meetingAtDeskUsers.has(uid)) {
        seats.push({
          person,
          occupied: true,
          meetingAtDesk: true,
          meetingName: meetingAtDeskUsers.get(uid),
        })
      } else {
        seats.push({ person, occupied: isHome })
      }
    }
    seatsByRoom.set(room.id, orderSeats(seats))
  }
  // The catch-all halls seat everyone, capped: online people order first so
  // the cap only ever hides the sleepiest tail of a whale org.
  const overflowByRoom: Record<string, number> = {}
  const seatHall = (roomId: string, people: Person[]): void => {
    if (people.length === 0) return
    const seats = orderSeats(people.map((person) => ({ person, occupied: true })))
    if (seats.length > HALL_CAP) {
      overflowByRoom[roomId] = seats.length - HALL_CAP
      seats.length = HALL_CAP
    }
    seatsByRoom.set(roomId, seats)
  }
  seatHall(LOBBY_ID, lobby)
  seatHall(AGENTS_ID, agentBay)
  // Conference-table seats: the attendees of each meeting that claimed a room.
  tableMeetings.forEach((tm, i) => {
    const seats: Seat[] = []
    for (const uid of tm.userIds) {
      const person = personById.get(uid)
      if (!person) continue
      seats.push({
        person,
        occupied: true,
        atMeetingTable: true,
        meetingName: tm.meeting.name,
      })
    }
    seatsByRoom.set(`${MEETING_PREFIX}${i}`, seats)
  })

  // A small bank of shared meeting rooms leads the floor; used ones host a live
  // meeting, idle ones read as "Available". Then team rooms, DM booths, Commons.
  const roomDefs: Array<{
    id: string
    name: string
    category: string
    meeting?: Meeting | null
  }> = []
  for (let i = 0; i < MEETING_TABLES; i += 1) {
    const tm = tableMeetings[i]
    roomDefs.push({
      id: `${MEETING_PREFIX}${i}`,
      name: tm ? tm.meeting.name : 'Meeting Room',
      category: 'MEETING',
      meeting: tm ? tm.meeting : null,
    })
  }
  for (const room of teamRooms) {
    roomDefs.push({ id: room.id, name: room.name, category: room.category })
  }
  if (lobby.length > 0) {
    roomDefs.push({ id: LOBBY_ID, name: 'Commons', category: 'LOBBY' })
  }
  if (agentBay.length > 0) {
    roomDefs.push({ id: AGENTS_ID, name: 'Agent Bay', category: 'AGENTS' })
  }
  for (const room of dmRooms) {
    roomDefs.push({ id: room.id, name: room.name, category: 'DM' })
  }
  if (roomDefs.length === 0) {
    roomDefs.push({ id: LOBBY_ID, name: 'Commons', category: 'LOBBY' })
    seatsByRoom.set(
      LOBBY_ID,
      orderSeats(snapshot.people.map((person) => ({ person, occupied: true }))),
    )
  }

  const seatCount = (id: string) => seatsByRoom.get(id)?.length ?? 1
  const sized = roomDefs.map((def) => {
    if (def.category === 'DM') {
      return { def, cols: 1, boardH: 0, w: DM_W, h: DM_H }
    }
    if (def.category === 'MEETING') {
      // Size each meeting room to fit its attendees; idle rooms stay compact.
      // A board strip sits between the header and the floor (mirrors TEAM rooms).
      const n = seatsByRoom.get(def.id)?.length ?? 0
      const cols = Math.max(2, Math.ceil(Math.sqrt(Math.max(1, n))))
      const rows = Math.max(2, Math.ceil(Math.max(1, n) / cols))
      return {
        def,
        cols,
        boardH: MEETING_BOARD_H,
        w: ROOM_PAD * 2 + cols * DESK,
        h: HEADER + MEETING_BOARD_H + ROOM_PAD * 2 + rows * DESK,
      }
    }
    // Team rooms reserve one extra grid slot for the "+ hire an agent" desk.
    const count = Math.max(1, seatCount(def.id) + (def.category === 'TEAM' ? 1 : 0))
    const cols = Math.ceil(Math.sqrt(count))
    const rows = Math.ceil(count / cols)
    const boardH = def.category === 'TEAM' ? BOARD_H : 0
    return {
      def,
      cols,
      boardH,
      w: ROOM_PAD * 2 + cols * DESK,
      h: HEADER + boardH + ROOM_PAD * 2 + rows * DESK,
    }
  })

  const meetingSized = sized.filter((s) => s.def.category === 'MEETING')
  const teamSized = sized.filter(
    (s) => s.def.category !== 'DM' && s.def.category !== 'MEETING',
  )
  const dmSized = sized.filter((s) => s.def.category === 'DM')

  /**
   * Lay the whole floor out for one candidate team-row width and return the
   * room rects. This IS the layout (not an approximation of it) — the aspect
   * search below runs it once per candidate and keeps the best-scoring floor,
   * so the simulation can never drift from the real placement.
   *
   * Shape: team rooms (+ Commons) in a grid with the shared meeting rooms as
   * a centred band through the MIDDLE (never the top); then the 1:1 wing as a
   * BLOCK anchored to the floor's bottom-RIGHT corner (Malek, 2026-07-11) —
   * it tucks up into free corner space beside short bottom rows and grows
   * from that corner, instead of stretching a full-width band under
   * everything.
   */
  const layoutFloor = (perRow: number): RoomLayout[] => {
    const rooms: RoomLayout[] = []
    let cursorX = MARGIN
    let cursorY = MARGIN
    let rowHeight = 0
    const place = (item: (typeof sized)[number]) => {
      rooms.push({
        id: item.def.id,
        name: item.def.name,
        category: item.def.category,
        x: cursorX,
        y: cursorY,
        w: item.w,
        h: item.h,
        boardH: item.boardH,
        meeting: item.def.meeting ?? null,
      })
      cursorX += item.w + GAP
      rowHeight = Math.max(rowHeight, item.h)
    }
    const newRow = (extra = GAP) => {
      cursorX = MARGIN
      cursorY += rowHeight + extra
      rowHeight = 0
    }

    const teamRows: Array<typeof teamSized> = []
    for (let i = 0; i < teamSized.length; i += perRow) {
      teamRows.push(teamSized.slice(i, i + perRow))
    }
    const midRow = Math.ceil(teamRows.length / 2)
    let gridRight = MARGIN
    const placeRow = (row: typeof teamSized): void => {
      for (const item of row) place(item)
      gridRight = Math.max(gridRight, cursorX - GAP)
      newRow()
    }
    teamRows.slice(0, midRow).forEach(placeRow)
    if (meetingSized.length > 0) {
      const bandW = meetingSized.reduce((sum, m) => sum + m.w + GAP, 0) - GAP
      // Centre the band within the wider of (team grid, the band itself) so it
      // stays centred even when the grid is empty or narrower than the band.
      const span = Math.max(gridRight - MARGIN, bandW)
      cursorX = Math.round(MARGIN + (span - bandW) / 2)
      for (const item of meetingSized) place(item)
      gridRight = Math.max(gridRight, cursorX - GAP)
      newRow(GAP * 1.4)
    }
    teamRows.slice(midRow).forEach(placeRow)

    // The 1:1 wing: a block in the bottom-right corner of the floor.
    if (dmSized.length > 0) {
      const floorRight = Math.max(
        gridRight,
        MARGIN + DM_W, // no team rooms at all — the wing IS the floor
      )
      const floorBottom = rooms.reduce((m, r) => Math.max(m, r.y + r.h), MARGIN)
      const floorW = floorRight - MARGIN
      // Block shape: ~40% of the floor's width (the "up to a quarter of the
      // office" corner), at least 2 columns when there's room for them.
      const cols = Math.max(
        1,
        Math.min(
          dmSized.length,
          Math.max(2, Math.round((floorW * 0.4 + GAP) / (DM_W + GAP))),
        ),
      )
      const blockRows = Math.ceil(dmSized.length / cols)
      const blockW = cols * (DM_W + GAP) - GAP
      const blockH = blockRows * (DM_H + GAP) - GAP
      const blockX = Math.max(MARGIN, floorRight - blockW)
      // Tuck the block upward into free corner space: it must only clear the
      // rooms it actually stands under, not the whole floor's bottom line.
      const clearBottom = rooms.reduce(
        (m, r) => (r.x + r.w > blockX - GAP ? Math.max(m, r.y + r.h) : m),
        MARGIN,
      )
      // Bottom-align with the floor when the corner is deep enough; otherwise
      // sit just below the rooms in the way (the floor grows downward).
      const blockY = Math.max(clearBottom + GAP * 1.7, floorBottom - blockH)
      dmSized.forEach((item, i) => {
        rooms.push({
          id: item.def.id,
          name: item.def.name,
          category: item.def.category,
          x: blockX + (i % cols) * (DM_W + GAP),
          y: blockY + Math.floor(i / cols) * (DM_H + GAP),
          w: item.w,
          h: item.h,
          boardH: item.boardH,
          meeting: item.def.meeting ?? null,
        })
      })
    }
    return rooms
  }

  // Pick the team-row width whose finished floor lands closest to the
  // screen's aspect ratio — rooms are taller than wide, so a count-square
  // grid always came out tall and thin (fine on phones, wasteful on desktop).
  // layoutFloor is cheap (dozens of rects), so every candidate runs for real.
  const bounds = (rs: RoomLayout[]): { w: number; h: number } => ({
    w: rs.reduce((m, r) => Math.max(m, r.x + r.w), MARGIN) - MARGIN,
    h: rs.reduce((m, r) => Math.max(m, r.y + r.h), MARGIN) - MARGIN,
  })
  let rooms = layoutFloor(Math.max(1, teamSized.length))
  {
    let bestScore = Infinity
    for (let candidate = 1; candidate <= Math.max(1, teamSized.length); candidate += 1) {
      const rs = layoutFloor(candidate)
      const { w, h } = bounds(rs)
      if (w <= 0 || h <= 0) continue
      // Log-ratio distance treats 2x too wide and 2x too tall as equally bad.
      const score = Math.abs(Math.log(w / h) - Math.log(TARGET_ASPECT))
      if (score < bestScore) {
        bestScore = score
        rooms = rs
      }
    }
  }

  // Apply the user's saved "arrange mode" drags BEFORE anything derives from
  // room positions (seats, hire desks, lounge, world bounds all follow).
  const offsets = getRoomOffsets(snapshot.organizationId)
  for (const room of rooms) {
    const o = offsets[room.id]
    if (o) {
      room.x += o.dx
      room.y += o.dy
    }
  }

  // Re-anchor the floor to the arranged rooms: dragging everything down/right
  // would otherwise leave a dead band between the walls and the first room
  // (and dragging up/left would push rooms off the board). A uniform shift
  // keeps the arrangement intact while the bounds hug it again.
  if (rooms.length > 0) {
    const minX = rooms.reduce((m, r) => Math.min(m, r.x), Infinity)
    const minY = rooms.reduce((m, r) => Math.min(m, r.y), Infinity)
    const shiftX = MARGIN - minX
    const shiftY = MARGIN - minY
    if (shiftX !== 0 || shiftY !== 0) {
      for (const room of rooms) {
        room.x += shiftX
        room.y += shiftY
      }
    }
  }

  const roomById: Record<string, RoomLayout> = {}
  for (const room of rooms) roomById[room.id] = room

  const placements: Placement[] = []
  const vacant: VacantSeat[] = []
  const byUser: Record<string, Placement> = {}
  const hireSpots: WorldLayout['hireSpots'] = []
  for (const room of rooms) {
    const seats = seatsByRoom.get(room.id) ?? []
    // TEAM rooms include the reserved hire slot in the grid (matches sizing).
    const isTeam = room.category === 'TEAM'
    const total = Math.max(1, seats.length + (isTeam ? 1 : 0))
    const cols = Math.ceil(Math.sqrt(total))
    seats.forEach((seat, index) => {
      let homeX: number
      let homeY: number
      if (room.category === 'MEETING' && seats.length <= MEETING_SEATS.length) {
        // Seat attendees exactly on the conference table's baked chairs,
        // mirroring drawMeetingRoom's table transform. The table drops BELOW the
        // board strip, so floorTop and the scale denominator both include boardH.
        const tScale = Math.min(
          (room.w * 0.76) / 132,
          (room.h - HEADER - room.boardH - 18) / 64,
          1,
        )
        const mFloorTop = room.y + HEADER + room.boardH
        const ccx = room.x + room.w / 2
        const ccy = mFloorTop + (room.h - HEADER - room.boardH) / 2 + 6
        const s = MEETING_SEATS[index] ?? [66, 32]
        homeX = ccx + (s[0] - 66) * tScale
        homeY = ccy + (s[1] - 32) * tScale
      } else {
        const col = index % cols
        const row = Math.floor(index / cols)
        homeX = room.x + ROOM_PAD + DESK / 2 + col * DESK
        homeY = room.y + HEADER + room.boardH + ROOM_PAD + DESK / 2 + row * DESK
      }
      const variant = seatVariant(seat.person.userId, room.id)
      if (seat.occupied) {
        const placement: Placement = {
          userId: seat.person.userId,
          roomId: room.id,
          homeX,
          homeY,
          variant,
          atMeetingTable: seat.atMeetingTable,
          meetingAtDesk: seat.meetingAtDesk,
          meetingName: seat.meetingName,
        }
        placements.push(placement)
        byUser[seat.person.userId] = placement
      } else {
        vacant.push({
          userId: seat.person.userId,
          name: seat.person.name,
          roomId: room.id,
          homeX,
          homeY,
          variant,
          inMeetingRoom: seat.inMeetingRoom,
          meetingName: seat.meetingName,
        })
      }
    })
    if (isTeam) {
      // The next grid slot after the last seat is the hire desk.
      const index = seats.length
      hireSpots.push({
        roomId: room.id,
        x: room.x + ROOM_PAD + DESK / 2 + (index % cols) * DESK,
        y:
          room.y +
          HEADER +
          room.boardH +
          ROOM_PAD +
          DESK / 2 +
          Math.floor(index / cols) * DESK,
      })
    }
  }

  // The water-cooler lounge sits in its own band BELOW every room (never inside
  // the grid), centred under the floor. Derived from the final rooms[] so it can
  // never overlap, then folded into world bounds so the stage frames it too.
  const lounge = buildLounge(rooms)

  const worldW =
    Math.max(
      rooms.reduce((m, r) => Math.max(m, r.x + r.w), 0),
      lounge.cx + lounge.w / 2,
    ) + MARGIN
  const worldH =
    Math.max(
      rooms.reduce((m, r) => Math.max(m, r.y + r.h), 0),
      lounge.cy + lounge.h / 2,
    ) + MARGIN

  return {
    rooms,
    placements,
    vacant,
    byUser,
    roomById,
    lounge,
    hireSpots,
    overflowByRoom,
    worldW,
    worldH,
  }
}

const LOUNGE_W = 120
const LOUNGE_H = 84

/** Centre the lounge under the full floor, in its own band below every room. */
function buildLounge(rooms: RoomLayout[]): LoungeLayout {
  const right = rooms.reduce((m, r) => Math.max(m, r.x + r.w), MARGIN)
  const bottom = rooms.reduce((m, r) => Math.max(m, r.y + r.h), MARGIN)
  // Integer coordinates — fractional centres blur the pixel-art sprites.
  const cx = Math.round((MARGIN + right) / 2)
  const cy = Math.round(bottom + GAP * 1.6 + LOUNGE_H / 2)
  return {
    cx,
    cy,
    w: LOUNGE_W,
    h: LOUNGE_H,
    coolerX: cx + LOUNGE_W / 2 - 14,
    coolerY: cy - 6,
    // Three loose standing spots around the rug (left, front, right-of-cooler).
    spots: [
      { x: cx - 30, y: cy + 6 },
      { x: cx + 2, y: cy + 14 },
      { x: cx + 26, y: cy - 2 },
    ],
  }
}
